/**
 * VHAKACHA CANONICAL LOCATIONS  (web app mirror)
 * ============================================================
 * Mirror of  provider app → src/lib/locations.ts
 * Keep both files in sync.
 *
 * `value` is the exact string stored on `listings.location`, and the
 * exact string this app matches against when filtering a destination
 * page. It is the BARE name — no ", Zimbabwe" suffix.
 * ============================================================
 */

export const LOCATIONS = [
  { value: 'Victoria Falls', label: 'Victoria Falls', region: 'Matabeleland North', coordinates: { latitude: -17.9243, longitude: 25.8572 }, aliases: ['Victoria Falls, Zimbabwe', 'Vic Falls', 'Mosi-oa-Tunya'] },
  { value: 'Hwange National Park', label: 'Hwange National Park', region: 'Matabeleland North', coordinates: { latitude: -18.6296, longitude: 26.7085 }, aliases: ['Hwange National Park, Zimbabwe', 'Hwange', 'Wankie'] },
  { value: 'Binga', label: 'Binga', region: 'Matabeleland North', coordinates: { latitude: -17.6206, longitude: 27.3406 }, aliases: ['Binga, Zimbabwe'] },
  { value: 'Kariba', label: 'Kariba', region: 'Mashonaland West', coordinates: { latitude: -16.5167, longitude: 28.8 }, aliases: ['Kariba, Zimbabwe', 'Lake Kariba'] },
  { value: 'Bumi Hills', label: 'Bumi Hills', region: 'Mashonaland West', coordinates: { latitude: -16.8167, longitude: 28.35 }, aliases: ['Bumi Hills, Zimbabwe', 'Bumi'] },
  { value: 'Matusadona National Park', label: 'Matusadona National Park', region: 'Mashonaland West', coordinates: { latitude: -16.85, longitude: 28.65 }, aliases: ['Matusadona National Park, Zimbabwe', 'Matusadona', 'Matusadonha'] },
  { value: 'Mana Pools', label: 'Mana Pools', region: 'Mashonaland West', coordinates: { latitude: -15.75, longitude: 29.35 }, aliases: ['Mana Pools, Zimbabwe', 'Mana Pools National Park'] },
  { value: 'Zvimba', label: 'Zvimba', region: 'Mashonaland West', coordinates: { latitude: -17.3667, longitude: 30.2 }, aliases: ['Zvimba, Mashonaland West', 'Zvimba, Zimbabwe'] },
  { value: 'Harare', label: 'Harare', region: 'Harare', coordinates: { latitude: -17.8252, longitude: 31.0335 }, aliases: ['Harare, Zimbabwe', 'Salisbury'] },
  { value: 'Lake Chivero', label: 'Lake Chivero', region: 'Mashonaland West', coordinates: { latitude: -17.9, longitude: 30.8 }, aliases: ['Lake Chivero, Zimbabwe', 'Chivero', 'Lake McIlwaine'] },
  { value: 'Bulawayo', label: 'Bulawayo', region: 'Bulawayo', coordinates: { latitude: -20.1325, longitude: 28.6265 }, aliases: ['Bulawayo, Zimbabwe'] },
  { value: 'Matobo Hills', label: 'Matobo Hills', region: 'Matabeleland South', coordinates: { latitude: -20.55, longitude: 28.5 }, aliases: ['Matobo Hills, Zimbabwe', 'Matopos', 'Matobo National Park'] },
  { value: 'Masvingo', label: 'Masvingo', region: 'Masvingo', coordinates: { latitude: -20.0637, longitude: 30.8277 }, aliases: ['Masvingo, Zimbabwe', 'Fort Victoria'] },
  { value: 'Great Zimbabwe', label: 'Great Zimbabwe', region: 'Masvingo', coordinates: { latitude: -20.2675, longitude: 30.9339 }, aliases: ['Great Zimbabwe, Zimbabwe', 'Great Zimbabwe Ruins'] },
  { value: 'Gonarezhou National Park', label: 'Gonarezhou National Park', region: 'Masvingo', coordinates: { latitude: -21.5, longitude: 31.75 }, aliases: ['Gonarezhou, Zimbabwe', 'Gonarezhou'] },
  { value: 'Chiredzi', label: 'Chiredzi', region: 'Masvingo', coordinates: { latitude: -21.05, longitude: 31.6667 }, aliases: ['Chiredzi, Zimbabwe'] },
  { value: 'Mutare', label: 'Mutare', region: 'Manicaland', coordinates: { latitude: -18.9707, longitude: 32.6709 }, aliases: ['Mutare, Zimbabwe', 'Umtali'] },
  { value: 'Nyanga', label: 'Nyanga', region: 'Manicaland', coordinates: { latitude: -18.2167, longitude: 32.75 }, aliases: ['Nyanga, Zimbabwe', 'Inyanga', 'Nyanga National Park'] },
  { value: 'Vumba', label: 'Vumba', region: 'Manicaland', coordinates: { latitude: -19.1167, longitude: 32.75 }, aliases: ['Vumba, Zimbabwe', 'Bvumba'] },
  { value: 'Mutarazi', label: 'Mutarazi Falls', region: 'Manicaland', coordinates: { latitude: -18.4833, longitude: 32.8333 }, aliases: ['Mutarazi, Zimbabwe', 'Mutarazi Falls', 'Mtarazi'] },
  { value: 'Chimanimani', label: 'Chimanimani', region: 'Manicaland', coordinates: { latitude: -19.8, longitude: 32.8667 }, aliases: ['Chimanimani, Zimbabwe', 'Chimanimani National Park'] },
];

export const LOCATION_VALUES = LOCATIONS.map((l) => l.value);

export function getLocation(value) {
  return LOCATIONS.find((l) => l.value === value);
}

export function isCanonicalLocation(value) {
  return LOCATION_VALUES.includes(value);
}

export function coordinatesFor(value) {
  const found = getLocation(value);
  return found ? found.coordinates : { latitude: -17.9243, longitude: 25.8572 };
}

/**
 * Best-effort mapping of an arbitrary historical string onto a canonical
 * value. Returns null when nothing matches, so callers can flag rather
 * than guess.
 */
export function resolveLocation(raw) {
  if (!raw) return null;
  const needle = String(raw).trim().toLowerCase();
  if (!needle) return null;

  const exact = LOCATIONS.find((l) => l.value.toLowerCase() === needle);
  if (exact) return exact.value;

  const aliased = LOCATIONS.find((l) => l.aliases.some((a) => a.toLowerCase() === needle));
  if (aliased) return aliased.value;

  const head = needle.split(',')[0].trim();
  const byHead = LOCATIONS.find(
    (l) => l.value.toLowerCase() === head || l.aliases.some((a) => a.toLowerCase() === head),
  );
  return byHead ? byHead.value : null;
}
